import { useMemo } from "react";
import { graphql } from "gatsby";
import {
  isArray,
  extract,
  GroupAdmission,
  SimplifyGatsbyQuery,
  ExistingThemes,
  RichTextType,
} from "@parksandresorts/core";
import WaitingRoom from "components/hoc/waiting-room";
import HalloweenBackground from "./backgrounds/halloween-background/halloween-background";
import WinterWonderlandBackground from "./backgrounds/winter-wonderland/winter-wonderland-background";
import {
  BannerData,
  CheckoutVariant,
} from "@parksandresorts/core/dist/types/state/group-admission/group-admission-types.js";

const getBackgroundElementForPageTheme = (pageTheme?: ExistingThemes) => {
  switch (pageTheme) {
    case "halloween": {
      return <HalloweenBackground />;
    }
    case "winter-wonderland": {
      return <WinterWonderlandBackground />;
    }
    default:
      return null;
  }
};

const getBackgroundElementModeForPageTheme = (pageTheme?: ExistingThemes) => {
  switch (pageTheme) {
    case "winter-wonderland": {
      return "WIDE";
    }
    default: {
      return "NARROW";
    }
  }
};

const GroupAdmissionBlock = (
  {
    contentful_id,
    purchaseFlowId,
    heroImageForSmallDevices,
    heroImageForLargeDevices,
    heroBackgroundImage,
    hideHeadline,
    description,
    showConcertsInCalendar,
    bannerText,
    bannerImage,
    bannerLink,
    openBannerLinkInNewTab,
    bannerDarkMode,
    dailyInformationLink,
    hideParkOpeningTimes,
    labelsOverride,
    soldOutEventDateContent,
    eventContainerLink,
    hideJetpassStep,
    selectTicketInsteadOfVariant,
    showScheduledTextBlocksInCalendar,
    jetpassMaxLimit,
    checkoutVariant,
    termsAndConditionsLink,
    customColors,
    priceLevelsWithMarking,
    additionalProductsAdult,
    additionalProductsChild,
    additionalProductsSenior,
    additionalProductsInfant,
  }: SimplifyGatsbyQuery<Queries.GroupAdmissionBlockFragment>,
  { waitRoomSecondsLeft, pageTheme }: { waitRoomSecondsLeft?: number; pageTheme?: ExistingThemes },
) => {
  const heroBackgroundImageUrl = heroBackgroundImage?.file.url;
  const bannerProps = {
    bannerText,
    bannerImage: extract(bannerImage, "file.url"),
    bannerLink,
    openBannerLinkInNewTab,
    bannerDarkMode,
  };

  const mappedLabels = useMemo(() => {
    if (!labelsOverride || !isArray(labelsOverride.list)) {
      return {};
    }

    return labelsOverride.list.reduce((labels: { [key: string]: string }, item) => {
      if (item.key && item.value) {
        labels[item.key] = item.value;
      }
      return labels;
    }, {});
  }, [labelsOverride]);

  const stepConfigOverride = {
    hideSelectJetPass: hideJetpassStep,
    selectTicketInsteadOfVariant: selectTicketInsteadOfVariant ?? false,
  };
  const backgroundElement = getBackgroundElementForPageTheme(pageTheme);
  const backgroundElementMode = getBackgroundElementModeForPageTheme(pageTheme);

  return (
    <section className="block block--no-margin" data-contentful-id={contentful_id}>
      <GroupAdmission
        purchaseFlowId={purchaseFlowId}
        heroImageForSmallDevices={heroImageForSmallDevices}
        heroImageForLargeDevices={heroImageForLargeDevices}
        heroBackgroundImageUrl={heroBackgroundImageUrl}
        hideHeadline={hideHeadline}
        description={description as RichTextType}
        showConcertsInCalendar={showConcertsInCalendar}
        bannerProps={bannerProps as BannerData}
        dailyInformationLink={dailyInformationLink}
        hideParkOpeningTimes={hideParkOpeningTimes}
        labelsOverride={mappedLabels}
        soldOutEventDateContent={soldOutEventDateContent}
        eventContainerLink={eventContainerLink}
        stepConfigOverride={stepConfigOverride}
        waitRoomSecondsLeft={waitRoomSecondsLeft}
        backgroundElement={backgroundElement}
        backgroundElementMode={backgroundElementMode}
        showScheduledTextBlocksInCalendar={showScheduledTextBlocksInCalendar}
        jetpassMaxLimit={jetpassMaxLimit}
        checkoutVariant={(checkoutVariant as CheckoutVariant) || undefined}
        termsAndConditionsLink={termsAndConditionsLink}
        customColors={customColors}
        priceLevelsWithMarking={priceLevelsWithMarking?.map((p) => Number(p)) ?? []}
        additionalProductsAdult={additionalProductsAdult}
        additionalProductsChild={additionalProductsChild}
        additionalProductsSenior={additionalProductsSenior}
        additionalProductsInfant={additionalProductsInfant}
      />
    </section>
  );
};

export default WaitingRoom(GroupAdmissionBlock);

export const fragmentGroupAdmissionBlock = graphql`
  fragment GroupAdmissionBlock on ContentfulGroupAdmissionBlock {
    __typename
    contentful_id
    purchaseFlowId
    heroImageForSmallDevices {
      title
      file {
        url
      }
    }
    heroImageForLargeDevices {
      title
      file {
        url
      }
    }
    heroBackgroundImage {
      file {
        url
      }
    }
    hideHeadline
    description {
      raw
      references {
        __typename
        ...RichTextFeature
        ...Link
        ...ContentPageLink
      }
    }
    showConcertsInCalendar
    bannerText {
      raw
    }
    bannerImage {
      file {
        url
      }
    }
    bannerLink {
      ...Link
    }
    openBannerLinkInNewTab
    bannerDarkMode
    dailyInformationLink {
      ...Link
    }
    hideParkOpeningTimes
    labelsOverride {
      list {
        key
        value
      }
    }
    enableWaitingRoom
    waitingRoomLogo {
      file {
        url
      }
    }
    soldOutEventDateContent {
      raw
      references {
        __typename
        ...Link
      }
    }
    eventContainerLink {
      __typename
      ...Link
    }
    hideJetpassStep
    connectedProduct {
      ...ProductBlock
    }
    selectTicketInsteadOfVariant
    showScheduledTextBlocksInCalendar
    jetpassMaxLimit
    checkoutVariant
    termsAndConditionsLink {
      ...Link
    }
    priceLevelsWithMarking
    customColors {
      primaryPricingTextColor
      primaryPricingBackgroundColor
      primaryPricingTextColorHover
      primaryPricingBackgroundColorHover
      primaryPricingTextColorSelected
      primaryPricingBackgroundColorSelected
      markedPricingTextColor
      markedPricingBackgroundColor
      markedPricingTextColorHover
      markedPricingBackgroundColorHover
      markedPricingTextColorSelected
      markedPricingBackgroundColorSelected
      primaryPricingSelectedDateMarkerTextColor
      primaryPricingSelectedDateMarkerBackgroundColor
      markedPricingSelectedDateMarkerTextColor
      markedPricingSelectedDateMarkerBackgroundColor
    }
    additionalProductsAdult {
      ...ProductBlock
    }
    additionalProductsChild {
      ...ProductBlock
    }
    additionalProductsSenior {
      ...ProductBlock
    }
    additionalProductsInfant {
      ...ProductBlock
    }
  }
`;
